import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FileText, Loader2, Plus, Trash2, ArrowRight, Lock, Unlock, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function MyReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);

  const load = () => api.get("/reports").then(({ data }) => setReports(data)).catch(() => setReports([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/reports/${id}`);
      setReports((r) => r.filter((x) => x.id !== id));
      toast.success("Report deleted");
    } catch {
      toast.error("Could not delete report");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">My Reports</h1>
          <p className="mt-1 text-slate-500">Your relocation match history.</p>
        </div>
        <Button onClick={() => navigate("/assessment")} className="h-11 gap-2 font-medium" data-testid="new-assessment-btn">
          <Plus className="h-4 w-4" /> New assessment
        </Button>
      </div>

      {reports === null ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : reports.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-dotted py-20 text-center">
          <FileText className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 font-medium">No reports yet</p>
          <p className="mt-1 text-sm text-slate-500">Take an assessment to generate your first report.</p>
          <Button onClick={() => navigate("/assessment")} className="mt-5 gap-2" data-testid="empty-new-assessment">
            <Plus className="h-4 w-4" /> Take assessment
          </Button>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {reports.map((r) => {
            const top = r.top_cities?.[0];
            const processing = r.status === "processing";
            return (
              <div key={r.id} data-testid={`report-card-${r.id}`} className="group overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 hover:border-slate-300">
                <div className="relative h-32 bg-slate-100">
                  {top?.image_url && <img src={top.image_url} alt="" className="h-full w-full object-cover" />}
                  <div className="absolute right-3 top-3">
                    {processing ? (
                      <Badge variant="secondary" className="gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Generating</Badge>
                    ) : r.unlocked ? (
                      <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600"><Unlock className="h-3 w-3" /> Unlocked</Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1"><Lock className="h-3 w-3" /> Preview</Badge>
                    )}
                  </div>
                  {r.ai_generated && !processing && (
                    <div className="absolute left-3 top-3"><Badge className="gap-1 bg-primary"><Sparkles className="h-3 w-3" /> AI</Badge></div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-semibold tracking-tight">{processing ? "Generating report…" : `${top?.name}, ${top?.country}`}</h3>
                    {!processing && <span className="font-heading text-lg font-semibold">{r.overall_match_score}%</span>}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" className="flex-1 gap-2" onClick={() => navigate(`/results/${r.id}`)} data-testid={`open-report-${r.id}`}>
                      {processing ? "View progress" : "View results"} <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={`delete-report-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this report?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(r.id)} data-testid={`confirm-delete-${r.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
